import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { useEffect, useRef, useState } from "react";

import type { Dokument } from "../api/typen";
import { useBlobUrl } from "../hooks/useBlobUrl";
import { Hinweis, Knopf, Spinner } from "./Basis";
import { FehlerGrenze } from "./FehlerGrenze";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

const ZOOM_STUFEN = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

function useBreite<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [breite, setBreite] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const beobachter = new ResizeObserver(([eintrag]) => {
      if (eintrag) setBreite(Math.floor(eintrag.contentRect.width));
    });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);
  return { ref, breite };
}

function Werkzeugleiste({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 bg-neutral-50 px-3 py-2">{children}</div>;
}

function ZoomKnoepfe({ zoom, setZoom }: { zoom: number; setZoom: (z: number) => void }) {
  const index = ZOOM_STUFEN.indexOf(zoom);
  return (
    <div className="flex items-center gap-1">
      <Knopf variante="geist" className="px-2 py-1" aria-label="Verkleinern" disabled={index <= 0} onClick={() => setZoom(ZOOM_STUFEN[index - 1] ?? zoom)}>
        −
      </Knopf>
      <span className="w-12 text-center text-xs tabular-nums text-neutral-600">{Math.round(zoom * 100)} %</span>
      <Knopf variante="geist" className="px-2 py-1" aria-label="Vergrößern" disabled={index >= ZOOM_STUFEN.length - 1} onClick={() => setZoom(ZOOM_STUFEN[index + 1] ?? zoom)}>
        +
      </Knopf>
    </div>
  );
}

function PdfAnsicht({ blob, url }: { blob: Blob; url: string }) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [seite, setSeite] = useState(1);
  const [zoom, setZoom] = useState(1);
  const leinwand = useRef<HTMLCanvasElement>(null);
  const { ref: behaelter, breite } = useBreite<HTMLDivElement>();

  useEffect(() => {
    let aktiv = true;
    let geladen: PDFDocumentProxy | null = null;
    setPdf(null);
    setFehler(null);
    setSeite(1);
    void blob.arrayBuffer().then((puffer) => {
      const aufgabe = pdfjs.getDocument({ data: new Uint8Array(puffer), isEvalSupported: false });
      aufgabe.promise
        .then((dokument) => {
          geladen = dokument;
          if (aktiv) setPdf(dokument);
          else void dokument.destroy();
        })
        .catch(() => aktiv && setFehler("PDF konnte nicht gelesen werden"));
    });
    return () => {
      aktiv = false;
      void geladen?.destroy();
    };
  }, [blob]);

  useEffect(() => {
    if (!pdf || !leinwand.current || breite === 0) return;
    let aufgabe: RenderTask | null = null;
    let abgebrochen = false;
    void pdf.getPage(seite).then((page) => {
      if (abgebrochen || !leinwand.current) return;
      const basis = page.getViewport({ scale: 1 });
      const skala = ((breite - 2) / basis.width) * zoom;
      const viewport = page.getViewport({ scale: skala });
      const dpr = window.devicePixelRatio || 1;
      const canvas = leinwand.current;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const kontext = canvas.getContext("2d");
      if (!kontext) return;
      aufgabe = page.render({ canvasContext: kontext, viewport, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined });
      aufgabe.promise.catch((e: unknown) => {
        if ((e as { name?: string }).name !== "RenderingCancelledException") setFehler("Seite konnte nicht dargestellt werden");
      });
    });
    return () => {
      abgebrochen = true;
      aufgabe?.cancel();
    };
  }, [pdf, seite, zoom, breite]);

  return (
    <div className="flex h-full flex-col">
      <Werkzeugleiste>
        <div className="flex items-center gap-1">
          <Knopf variante="geist" className="px-2 py-1" aria-label="Vorherige Seite" disabled={seite <= 1} onClick={() => setSeite((s) => s - 1)}>
            ◀
          </Knopf>
          <span className="text-xs tabular-nums text-neutral-600">
            Seite {seite} / {pdf?.numPages ?? "…"}
          </span>
          <Knopf variante="geist" className="px-2 py-1" aria-label="Nächste Seite" disabled={!pdf || seite >= pdf.numPages} onClick={() => setSeite((s) => s + 1)}>
            ▶
          </Knopf>
        </div>
        <ZoomKnoepfe zoom={zoom} setZoom={setZoom} />
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold underline underline-offset-2">
          Original öffnen ↗
        </a>
      </Werkzeugleiste>
      <div ref={behaelter} className="relative flex-1 overflow-auto bg-neutral-200/60 p-0">
        {fehler && <div className="p-4"><Hinweis art="fehler">{fehler}</Hinweis></div>}
        {!pdf && !fehler && <div className="flex h-64 items-center justify-center"><Spinner label="PDF wird geladen…" /></div>}
        <canvas ref={leinwand} className={`mx-auto block bg-white shadow ${pdf ? "" : "hidden"}`} />
      </div>
    </div>
  );
}

function BildAnsicht({ url, name }: { url: string; name: string }) {
  const [zoom, setZoom] = useState(1);
  const [drehung, setDrehung] = useState(0);
  return (
    <div className="flex h-full flex-col">
      <Werkzeugleiste>
        <Knopf variante="geist" className="px-2 py-1 text-xs" onClick={() => setDrehung((d) => (d + 90) % 360)}>
          ↻ Drehen
        </Knopf>
        <ZoomKnoepfe zoom={zoom} setZoom={setZoom} />
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold underline underline-offset-2">
          Original öffnen ↗
        </a>
      </Werkzeugleiste>
      <div className="flex-1 overflow-auto bg-neutral-200/60 p-2">
        <img
          src={url}
          alt={name}
          style={{ transform: `rotate(${drehung}deg)`, width: `${zoom * 100}%` }}
          className="mx-auto block max-w-none origin-center bg-white shadow transition-transform"
        />
      </div>
    </div>
  );
}

const WORD_TYPEN = ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/msword"];

/** Word-Dateien: keine Vorschau im Browser, nur Download. Die Werte stammen aus dem Text. */
function WordAnsicht({ url, name }: { url: string; name: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <span className="text-5xl" aria-hidden>📝</span>
      <p className="font-semibold">{name}</p>
      <p className="max-w-sm text-sm text-neutral-600">
        Word-Datei – eine Vorschau ist hier nicht möglich. Beim Auslesen nutzt die KI nur den Text; Bilder und
        Unterschriften in der Datei werden nicht erkannt.
      </p>
      <a href={url} download={name} className="rounded-lg bg-marke px-4 py-2 text-sm font-semibold text-white">
        Herunterladen
      </a>
    </div>
  );
}

export function DokumentVorschau({ dokument, hoehe = "h-[70vh]" }: { dokument: Dokument; hoehe?: string }) {
  const { url, blob, laedt, fehler } = useBlobUrl(dokument.vorschau_url);
  return (
    <div className={`overflow-hidden rounded-xl border border-neutral-200 bg-white ${hoehe}`}>
      {laedt && <div className="flex h-full items-center justify-center"><Spinner label="Dokument wird geladen…" /></div>}
      {fehler && <div className="p-4"><Hinweis art="fehler">{fehler.message}</Hinweis></div>}
      {url && blob && (
        <FehlerGrenze bereich="Vorschau">
          {WORD_TYPEN.includes(dokument.dateitype) ? (
            <WordAnsicht url={url} name={dokument.original_dateiname} />
          ) : dokument.dateitype === "application/pdf" ? (
            <PdfAnsicht blob={blob} url={url} />
          ) : (
            <BildAnsicht url={url} name={dokument.original_dateiname} />
          )}
        </FehlerGrenze>
      )}
    </div>
  );
}

export function Thumbnail({ dokument, groesse = "h-12 w-12" }: { dokument: Dokument; groesse?: string }) {
  const { url } = useBlobUrl(dokument.thumbnail_url);
  return url ? (
    <img src={url} alt="" className={`${groesse} shrink-0 rounded-md border border-neutral-200 object-cover`} />
  ) : (
    <span className={`${groesse} inline-flex shrink-0 items-center justify-center rounded-md border border-neutral-200 bg-neutral-50 text-lg`} aria-hidden>
      {WORD_TYPEN.includes(dokument.dateitype) ? "📝" : "📄"}
    </span>
  );
}
